import type { RacerSnapshot } from '../shared/types.js';
import type { StreetTourId } from '../shared/night-tour-rivals.js';
import {
  completeRaceProgression,
  loadProgression,
  type CareerRaceContext,
  type ProgressionState,
  type ProgressionUpdate,
  type RaceRunStats,
} from './progression.js';
import {
  loadVehicleMastery,
  recordVehicleMastery,
  type VehicleMasteryState,
  type VehicleMasteryUpdate,
} from './vehicle-mastery.js';
import {
  loadDailyChallengeState,
  recordDailyChallengeResult,
  type DailyChallengeState,
} from './daily-challenge.js';
import {
  loadNightTour,
  nightTourStateForStart,
  recordNightTourResult,
  selectNightTour,
  type NightTourState,
} from './night-tour.js';
import {
  appendLocalCareerRaceId,
  localCareerProcessedRaceIds,
  writeLocalCareerEnvelope,
} from './local-career-storage.js';
import { withOriginLock } from './origin-lock.js';

const CAREER_LOCK_NAME = 'nightshift:local-career';
const CAREER_LEASE_KEY = 'nightshift:local-career-lease:v1';
const CAREER_LEASE_MS = 4_000;
const DEFAULT_COMMIT_RETRY_DELAYS_MS = [80, 260, 900] as const;
const pendingRetriedCommits = new Map<string, Promise<LocalCareerCommitResult>>();

export interface LocalCareerCommitRequest {
  racer: RacerSnapshot;
  racers: readonly RacerSnapshot[];
  raceId: string;
  run: RaceRunStats;
  career?: {
    totalRacers: number;
    context: Partial<CareerRaceContext>;
  };
  daily?: {
    challengeId: string;
    lapMs: number | null;
    now?: number;
  };
  tour?: {
    tourId: StreetTourId;
    stage: number;
  };
}

export interface LocalCareerCommitResult {
  progression: ProgressionState;
  progressionUpdate: ProgressionUpdate | null;
  vehicleMastery: VehicleMasteryState;
  vehicleMasteryUpdate: VehicleMasteryUpdate | null;
  dailyChallenges: DailyChallengeState;
  nightTour: NightTourState;
}

export interface LocalCareerCommitRetryOptions {
  retryDelaysMs?: readonly number[];
}

/**
 * Banks every accountless result against the newest durable state. Web Locks
 * serialize tabs where available; an atomic IndexedDB lease covers older or
 * insecure Chromium builds without blocking either renderer thread.
 */
export function commitLocalCareerResult(
  request: LocalCareerCommitRequest,
): Promise<LocalCareerCommitResult> {
  return withCareerLock(() => {
    let progression = loadProgression();
    let vehicleMastery = loadVehicleMastery();
    let dailyChallenges = loadDailyChallengeState();
    let nightTour = loadNightTour();
    let progressionUpdate: ProgressionUpdate | null = null;
    let vehicleMasteryUpdate: VehicleMasteryUpdate | null = null;
    let processedRaceIds = localCareerProcessedRaceIds();
    const globallyProcessed = validRaceId(request.raceId)
      && processedRaceIds.includes(request.raceId);

    if (request.daily && !globallyProcessed) {
      dailyChallenges = recordDailyChallengeResult(
        dailyChallenges,
        request.daily.challengeId,
        request.daily.lapMs,
        request.daily.now,
        request.raceId,
        false,
      );
    }
    if (request.career) {
      if (globallyProcessed) {
        progression = {
          ...progression,
          awardedRaceIds: appendBoundedRaceId(progression.awardedRaceIds, request.raceId, 128),
        };
        vehicleMastery = {
          ...vehicleMastery,
          awardedRaceIds: appendBoundedRaceId(vehicleMastery.awardedRaceIds, request.raceId, 192),
        };
      }
      progressionUpdate = completeRaceProgression(
        progression,
        request.racer,
        request.career.totalRacers,
        request.run,
        request.raceId,
        request.career.context,
        false,
      );
      progression = progressionUpdate.state;
      const mastery = recordVehicleMastery(
        vehicleMastery,
        request.racer,
        request.career.totalRacers,
        request.run,
        request.raceId,
        false,
      );
      vehicleMastery = mastery.state;
      vehicleMasteryUpdate = mastery.update;
    }
    if (request.tour && !globallyProcessed) {
      if (nightTour.tourId !== request.tour.tourId) {
        nightTour = selectNightTour(nightTour, request.tour.tourId);
      }
      const durableTour = nightTour;
      const repeatingCompletedTour = request.tour.stage === 0 && nightTour.completed;
      // A repeat tour is opened only by successfully banking its new stage 0.
      // Until then the completed championship remains durable and can be shown
      // or retried after a failed race launch.
      if (repeatingCompletedTour) {
        nightTour = nightTourStateForStart(nightTour);
      }
      const recordedTour = recordNightTourResult(
        nightTour,
        request.raceId,
        request.tour.stage,
        request.racer,
        request.racers,
        false,
      );
      nightTour = repeatingCompletedTour && recordedTour === nightTour
        ? durableTour
        : recordedTour;
    }
    if (!globallyProcessed && validRaceId(request.raceId)) {
      processedRaceIds = appendLocalCareerRaceId(processedRaceIds, request.raceId);
    }

    const result = {
      progression,
      progressionUpdate,
      vehicleMastery,
      vehicleMasteryUpdate,
      dailyChallenges,
      nightTour,
    };
    persistCareerResult(result, processedRaceIds);
    return result;
  });
}

/**
 * Retries a transient browser-storage failure without asking the simulation to
 * emit its one-shot finished transition again. Race IDs make every retry
 * idempotent, and duplicate callers share one in-flight retry chain.
 */
export function commitLocalCareerResultWithRetry(
  request: LocalCareerCommitRequest,
  options: LocalCareerCommitRetryOptions = {},
): Promise<LocalCareerCommitResult> {
  const key = validRaceId(request.raceId) ? request.raceId : '';
  const existing = key ? pendingRetriedCommits.get(key) : undefined;
  if (existing) return existing;
  const retryDelays = options.retryDelaysMs ?? DEFAULT_COMMIT_RETRY_DELAYS_MS;
  const pending = (async () => {
    let lastError: unknown;
    for (let attempt = 0; attempt <= retryDelays.length; attempt += 1) {
      try {
        return await commitLocalCareerResult(request);
      } catch (error) {
        lastError = error;
        const delayMs = retryDelays[attempt];
        if (delayMs === undefined) break;
        await delay(Math.max(0, Number.isFinite(delayMs) ? delayMs : 0));
      }
    }
    throw lastError instanceof Error ? lastError : new Error('Local career save failed');
  })();
  if (key) {
    pendingRetriedCommits.set(key, pending);
    void pending.finally(() => {
      if (pendingRetriedCommits.get(key) === pending) pendingRetriedCommits.delete(key);
    }).catch(() => undefined);
  }
  return pending;
}

/** Serialises every non-race NIGHT TOUR mutation against finished-race commits. */
export function mutateLocalNightTour(
  transform: (current: NightTourState) => NightTourState,
): Promise<NightTourState> {
  return withCareerLock(() => {
    const result: LocalCareerCommitResult = {
      progression: loadProgression(),
      progressionUpdate: null,
      vehicleMastery: loadVehicleMastery(),
      vehicleMasteryUpdate: null,
      dailyChallenges: loadDailyChallengeState(),
      nightTour: transform(loadNightTour()),
    };
    persistCareerResult(result, localCareerProcessedRaceIds());
    return result.nightTour;
  });
}

function withCareerLock<T>(operation: () => T): Promise<T> {
  return withOriginLock(CAREER_LOCK_NAME, CAREER_LEASE_KEY, operation, {
    leaseMs: CAREER_LEASE_MS,
    maximumWaitMs: CAREER_LEASE_MS + 1_000,
  });
}

function persistCareerResult(
  result: LocalCareerCommitResult,
  processedRaceIds: readonly string[],
): void {
  if (typeof localStorage === 'undefined') return;
  if (!writeLocalCareerEnvelope({
    progression: result.progression,
    vehicleMastery: result.vehicleMastery,
    dailyChallenges: result.dailyChallenges,
    nightTour: result.nightTour,
    processedRaceIds,
  })) {
    throw new Error('Local career storage is unavailable');
  }
}

function appendBoundedRaceId(
  values: readonly string[],
  raceId: string,
  maximum: number,
): string[] {
  return [...values.filter((value) => value !== raceId), raceId].slice(-maximum);
}

function validRaceId(value: unknown): value is string {
  return typeof value === 'string' && /^[a-zA-Z0-9:_-]{1,160}$/.test(value);
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => globalThis.setTimeout(resolve, milliseconds));
}
